import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart3, MessageSquare, Users, Clock, Bot, TrendingUp,
  ArrowUp, ArrowDown, Loader2, Zap
} from 'lucide-react';
import api from '@/services/api';

// Pure SVG mini bar chart
const MiniBarChart = ({ data, dataKey, color = '#3b82f6', height = 200 }) => {
  if (!data || data.length === 0) return <div className="h-48 flex items-center justify-center text-slate-400 text-sm">No data yet</div>;
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const barW = Math.max(12, Math.floor(680 / data.length) - 4);

  return (
    <svg viewBox={`0 0 ${data.length * (barW + 4)} ${height + 24}`} className="w-full" style={{ maxHeight: height + 24 }}>
      {data.map((d, i) => {
        const h = (values[i] / max) * height;
        return (
          <g key={`bar-${i}-${values[i]}`}>
            <rect 
              x={i * (barW + 4)} y={height - h} width={barW} height={Math.max(h, 2)} 
              rx={3} fill={color} opacity={0.85}
            />
            <title>{d.date}: {values[i]}</title>
            {i % Math.ceil(data.length / 7) === 0 && (
              <text x={i * (barW + 4) + barW / 2} y={height + 16} textAnchor="middle" className="text-[10px] fill-slate-400">
                {d.day}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// SVG sparkline
const Sparkline = ({ data, dataKey, color = '#3b82f6', w = 120, h = 32 }) => {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / range) * (h - 4) - 2}`).join(' ');
  
  return (
    <svg width={w} height={h} className="inline-block">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [chatbots, setChatbots] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState('all');
  const [dateRange, setDateRange] = useState('30d');
  const [stats, setStats] = useState({
    total_conversations: 0, total_messages: 0, avg_response_time: 0,
    leads_captured: 0, active_users: 0, knowledge_queries: 0
  });
  const [dailyStats, setDailyStats] = useState([]);

  useEffect(() => { loadChatbots(); }, []);
  useEffect(() => { loadStats(); }, [selectedChatbot, dateRange, chatbots]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadChatbots = async () => {
    try {
      const response = await api.get('/chatbots');
      setChatbots(Array.isArray(response.data) ? response.data : response.data.chatbots || []);
    } catch (error) {
      console.error('Error loading chatbots:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const chatbotId = selectedChatbot === 'all' ? null : selectedChatbot;
      const endpoint = chatbotId 
        ? `/analytics/chatbot/${chatbotId}?range=${dateRange}`
        : `/analytics/overview?range=${dateRange}`;
      
      const response = await api.get(endpoint);
      setStats(response.data.stats || stats);
      setDailyStats(response.data.daily || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const statCards = [
    { title: 'Total Conversations', value: stats.total_conversations, icon: MessageSquare, color: 'blue', dataKey: 'conversations' },
    { title: 'Messages Exchanged', value: stats.total_messages, icon: Zap, color: 'purple', dataKey: 'messages' },
    { title: 'Avg Response Time', value: `${stats.avg_response_time}s`, icon: Clock, color: 'emerald', dataKey: null },
    { title: 'Leads Captured', value: stats.leads_captured, icon: Users, color: 'amber', dataKey: 'leads' },
    { title: 'Active Sessions', value: stats.active_users, icon: TrendingUp, color: 'rose', dataKey: null },
    { title: 'Knowledge Queries', value: stats.knowledge_queries, icon: BarChart3, color: 'indigo', dataKey: 'messages' },
  ];

  const colorMap = {
    blue: { bg: 'bg-blue-100 text-blue-600', hex: '#3b82f6' },
    purple: { bg: 'bg-purple-100 text-purple-600', hex: '#8b5cf6' },
    emerald: { bg: 'bg-emerald-100 text-emerald-600', hex: '#10b981' },
    amber: { bg: 'bg-amber-100 text-amber-600', hex: '#f59e0b' },
    rose: { bg: 'bg-rose-100 text-rose-600', hex: '#f43f5e' },
    indigo: { bg: 'bg-indigo-100 text-indigo-600', hex: '#6366f1' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const totalMsgs = dailyStats.reduce((s, d) => s + (d.messages || 0), 0);
  const prevHalf = dailyStats.slice(0, Math.floor(dailyStats.length / 2));
  const recentHalf = dailyStats.slice(Math.floor(dailyStats.length / 2));
  const prevTotal = prevHalf.reduce((s, d) => s + (d.messages || 0), 0);
  const recentTotal = recentHalf.reduce((s, d) => s + (d.messages || 0), 0);
  const trendPct = prevTotal > 0 ? (((recentTotal - prevTotal) / prevTotal) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-indigo-600" />
            Analytics Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Track AI agent performance and user engagement</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedChatbot} onValueChange={setSelectedChatbot}>
            <SelectTrigger className="w-[200px]" data-testid="chatbot-filter">
              <SelectValue placeholder="All AI Employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All AI Employees</SelectItem>
              {chatbots.map((bot) => (
                <SelectItem key={bot.chatbot_id} value={bot.chatbot_id}>
                  <span className="flex items-center gap-2"><Bot className="w-4 h-4" />{bot.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]" data-testid="date-range-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.title} className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div className={`p-2 sm:p-3 rounded-xl ${colorMap[stat.color].bg}`}>
                  <stat.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                {stat.dataKey && dailyStats.length > 1 && (
                  <Sparkline data={dailyStats} dataKey={stat.dataKey} color={colorMap[stat.color].hex} />
                )}
              </div>
              <div className="mt-3 sm:mt-4">
                <p className="text-2xl sm:text-3xl font-bold text-slate-900">{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</p>
                <p className="text-sm text-slate-600 mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Performance Chart */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Messages Over Time
              </CardTitle>
              <CardDescription>Daily message volume for the selected period</CardDescription>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-600">{totalMsgs.toLocaleString()} total</span>
              {Number(trendPct) !== 0 && (
                <Badge className={Number(trendPct) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                  {Number(trendPct) >= 0 ? <ArrowUp className="w-3 h-3 mr-0.5 inline" /> : <ArrowDown className="w-3 h-3 mr-0.5 inline" />}
                  {Math.abs(Number(trendPct))}%
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="p-2 bg-slate-50 rounded-lg border border-slate-100" data-testid="messages-chart">
            <MiniBarChart data={dailyStats} dataKey="messages" color="#3b82f6" height={180} />
          </div>
        </CardContent>
      </Card>

      {/* Conversations & Leads Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Conversations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-2 bg-blue-50/50 rounded-lg border border-blue-100" data-testid="conversations-chart">
              <MiniBarChart data={dailyStats} dataKey="conversations" color="#6366f1" height={140} />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Leads Captured
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-2 bg-emerald-50/50 rounded-lg border border-emerald-100" data-testid="leads-chart">
              <MiniBarChart data={dailyStats} dataKey="leads" color="#10b981" height={140} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Performing AI Employees */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" />
            AI Employees
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {chatbots.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No AI agents yet</p>
            ) : (
              chatbots.slice(0, 5).map((bot, idx) => (
                <div key={bot.chatbot_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">{idx + 1}</span>
                    <span className="font-medium text-slate-900">{bot.name}</span>
                  </div>
                  <Badge className={bot.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                    {bot.status}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPage;
